/**
 * ============================================================================
 * ProgramCard2 — Controller (client)
 * ============================================================================
 * Purpose: Own click / modal / analytics actions for the program card.
 * No JSX — view consumes the returned ViewModel.
 * ============================================================================
 */

"use client";

import { useCallback, useMemo } from "react";
import { useModalDispatch } from "@/context/ModalContext";
import { IModalActions, ModalTypes } from "@/types/modal";
import { pushToDataLayer } from "@/helpers/gtag";
import { getGlobalStateValue } from "@/context/utils";
import { warmLeadModals } from "@/helpers/leadModalGate";
import { mapProgramCard2Display } from "../mapper/program-card2.mapper";
import type {
  ProgramCard2Props,
  ProgramCard2ViewModel,
} from "../model/program-card2.types";

export function useProgramCard2Controller(
  props: ProgramCard2Props,
): ProgramCard2ViewModel {
  const {
    onClickHandler,
    isProgramList = false,
    isAffiliatePage = false,
    associatedPrograms = [],
    isProgramListPage = false,
    isLandingPage = false,
  } = props;

  const modalDispatch = useModalDispatch();

  const display = useMemo(() => mapProgramCard2Display(props), [
    props.basicDetails,
    props.fees,
    props.duration,
    props.country,
    props.isCertificationCard,
    props.industryAligned,
    props.isSchoolOfAIList,
    props.isProgramList,
    props.isAffiliatePage,
    props.id,
    props.associatedPrograms,
    props.isProgramListPage,
    props.isLandingPage,
    props.corporateOffer,
    props.discountedFeesInr,
  ]);

  const {
    brochureLink,
    urlKey,
    programHeading,
    programType,
  } = display;

  const onExploreClick = useCallback(() => {
    pushToDataLayer({
      event: "program_card_clicked",
      section_name: "program section",
      program_name: programHeading,
      page_url: window?.location?.href,
    });

    // Match preProd behavior: affiliate, landing, and program-list pages
    // all route via onClickHandler instead of default card navigation.
    if (isAffiliatePage || isLandingPage || isProgramListPage) {
      onClickHandler?.(urlKey);
    }
  }, [
    programHeading,
    isAffiliatePage,
    isLandingPage,
    isProgramListPage,
    onClickHandler,
    urlKey,
  ]);

  const isCorporateBrochure = Boolean(props.corporateOffer?.name);
  const corporateAmsId = props.amsId || props.corporateOffer?.amsId || "";

  const onBrochureClick = useCallback(() => {
    warmLeadModals();
    modalDispatch({
      type: IModalActions.OpenModal,
      data: {
        modalType: ModalTypes.ConnectModalMba,
        modalData: {
          rfiCase: "DOWNLOAD_BROCHURE_CASE",
          attachmentURL: brochureLink,
          associatedPrograms,
          modalVariant: "downloadBrochure",
          ...(isCorporateBrochure ? { isCorporateBrochure: true } : {}),
          ...(corporateAmsId
            ? { metaApiParams: { AMSID: String(corporateAmsId) } }
            : {}),
        },
      },
    });

    setTimeout(() => {
      pushToDataLayer({
        event: "program_card_clicked",
        section_name: "program section",
        cta_text: isCorporateBrochure ? "Download Brochure" : "Get Brochure",
        program_name: programHeading,
        page_url: window?.location?.href,
      });

      setTimeout(() => {
      window?.webengage?.track("Download Brochure Clicked", {
        program_name: programHeading,
        "Program Type": programType,
        "Page Type": getGlobalStateValue("pageType") || "Home Page",
        section_name: "program section",
      });
      }, 0);
    }, 1500);
  }, [
    brochureLink,
    associatedPrograms,
    isCorporateBrochure,
    corporateAmsId,
    modalDispatch,
    programHeading,
    programType,
  ]);

  const onCtaClick = useCallback(() => {
    if (isProgramList && !isProgramListPage) {
      onBrochureClick();
    } else {
      onExploreClick();
    }
  }, [isProgramList, isProgramListPage, onBrochureClick, onExploreClick]);

  const stopCardNavigation = useCallback((event) => {
    event.preventDefault();
    event.stopPropagation();
  }, []);

  return {
    ...display,
    onExploreClick,
    onBrochureClick,
    onCtaClick,
    stopCardNavigation,
  };
}
